import { Injectable } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, debounceTime, tap } from 'rxjs';
import { ToastService } from 'src/app/core/services/toast.service';
import { FilterResponse, PersonQuery } from 'src/app/models/FilterResponse';
import { ROWS_PER_PAGE_DEFAULT, ROWS_PER_PAGE_OPTIONS } from 'src/app/shared/constants/common.constants';
import { CardRequestService } from './card-request.service';

@Injectable({
    providedIn: 'root'
})
export class CardRequestsFilterService {

    form: FormGroup;
    isLoading = true;
    searchingByColumnFilter = false;
    lastSearchTime: Date;
    private readonly notificationSubject = new Subject<FilterResponse>();
    globalSearchSubject = new Subject<{ inputValue: string, inputFallBackReference: HTMLInputElement }>();
    private lastInputReference: HTMLInputElement;

    rowsPerPage = ROWS_PER_PAGE_DEFAULT;
    rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS;
    sortField = new BehaviorSubject<string>('');
    sortOrder = new BehaviorSubject<number>(1);

    private cachedFilterResponse: FilterResponse;
    query = '';

    isInitSearch = false;

    constructor(
        private readonly fb: FormBuilder,
		private readonly cardRequestService: CardRequestService,
		private readonly toastService: ToastService,
		private readonly translate: TranslateService
    ) {
        this.form = this.fb.group({
            queryId: [''],
            queryType: [''],
            personId: [''],
            firstName: [''],
            email: [''],
            phoneNumber: [''],
            personType: [''],
            startDate: [''],
            endDate: [''],
            requestReference: [''],
            status: [],
            substatus: ['']
        }, { validators: this.datesValidator('startDate', 'endDate') });

        // Subject para las busquedas globales
        this.globalSearchSubject.pipe(
            tap(() => this.isLoading = true),
            debounceTime(1000) // Espera 1000ms después de que el usuario deje de escribir
        ).subscribe((input) => {
            this.globalSearch(input.inputValue);
            this.lastInputReference = input.inputFallBackReference;
        });

        // debounceTime propaga que se ha activado algun filtro de nuestro formulario.
        for (const key of Object.keys(this.form.controls))
            this.form.get(key)?.valueChanges.pipe(
                tap(() => this.isLoading = true),
                debounceTime(300)
            ).subscribe(() => this.search());
    }

    async search(forceGetAllRequests?: boolean): Promise<void> {
        let filterOutput: FilterResponse;
        if (this.form.valid) {
            this.isLoading = true;
            try {
                await this.getDefaultFilterResponse(forceGetAllRequests);
                filterOutput = this.filterRequests(this.form);
                this.notificationSubject.next(filterOutput);
            } catch (e) {
                if (e?.status !== 403 && e?.status !== 440) {
                    this.toastService.showError(`Error ${e.status} (${e.statusText})`, `${e.message}`, false);
                }
            }
        }
        else {
            this.toastService.showError('TOAST.INVALID', 'FILTER.FILTER_FORM_INVALID', true);
        }
        this.isLoading = false;
        this.searchingByColumnFilter = false;
    }

    private async getDefaultFilterResponse(forceGetAllRequests?: boolean): Promise<void> {
        if (forceGetAllRequests || this.cachedFilterResponse == null) {
            this.cachedFilterResponse = await this.cardRequestService.getAllRequests();
            this.lastSearchTime = new Date();
        }
    }

    filterRequests(form: FormGroup): FilterResponse {
        // aquí ira la query con el filtro
        //return await this.api.post<FilterResponse>(`requests/filter-requests`, {});
        return this.clientSideFilter(form);
    }

    clientSideFilter(form: FormGroup): FilterResponse {
        let filteredPersonQueries: PersonQuery[] = [];

        // FILTERS
        for (const personQuery of this.cachedFilterResponse.requestList) {
            if (
                this.evaluateStringFormControl(form.get('queryId'), personQuery.queryId) &&
				// this.evaluateStringFormControl(form.get('queryType'), personQuery.queryType) &&	// TODO: deccoment this line when the API is ready
				this.evaluateStringFormControl(form.get('personId'), personQuery.personId || '') &&
				this.evaluateStringFormControl(form.get('phoneNumber'), personQuery.phoneNumber) &&
				this.evaluateStringFormControl(form.get('personType'), personQuery.personType) &&
                this.evaluateStringFormControl(form.get('requestReference'), personQuery.requestReference ?? '') &&
				this.evaluateStringFormControl(form.get('status'), personQuery.status) &&
				this.evaluateStringFormControl(form.get('substatus'), this.translate.instant(`SUBSTATUS.${personQuery.substatus ?? ''}`)) &&
				this.evaluateDateFormControl(form.get('startDate'), form.get('endDate'), personQuery)
            ) {
                filteredPersonQueries.push(personQuery);
            }
        }

        // GLOBAL SEARCH
        if (this?.query != null || this?.query != '') {
            filteredPersonQueries = this.searchResultsFilter(filteredPersonQueries);
        }

        return {
            requestList: filteredPersonQueries,
            requestPerPage: 0,
            fromIndex: 0,
            toIndex: 0,
            pageNumber: 0,
        };
    }

    private searchResultsFilter(inputRequests: PersonQuery[]): PersonQuery[] {
        const filteredPersonQueries: PersonQuery[] = [];
        for (const request of inputRequests) {
            const translatedSubstatus = this.translate.instant(`SUBSTATUS.${request.substatus ?? ''}`);
            const valuesToCheck = [request.queryId, request.personId, request.phoneNumber, request.requestReference, translatedSubstatus];

            for (const value of valuesToCheck) {
                if (value && value.toString().toLowerCase().includes(this.query.toLocaleLowerCase())) {
                    filteredPersonQueries.push(request);
                    break;
                }
            }
        }

        return filteredPersonQueries;
    }

    // Busca en los resultados actuales. Es compatible con los filtros.
    globalSearch(query: string): void {
        this.isLoading = true;

        this.query = query;
        const filterOutput = this.filterRequests(this.form);

        this.notificationSubject.next(filterOutput);
        this.isLoading = false;
    }

    filterHandler(): Observable<FilterResponse> {
        return this.notificationSubject.asObservable();
    }

    datesValidator(startDate: string, endDate: string): ValidatorFn {
        return (formGroup: AbstractControl): ValidationErrors | null => {
            const start = formGroup.get(startDate)?.value;
            const end = formGroup.get(endDate)?.value;

            if (!start || !end) {
                return null;
            }
            return new Date(start) <= new Date(end) ? null : { datesValidator: true };
        };
    }


    // Devolver si el campo cumple con la condicion del filtro.
    private evaluateStringFormControl(formControl: AbstractControl, valueToCheck: string): boolean {
        if (formControl.value != null && formControl.value != '') {
            return (valueToCheck ?? '').toLowerCase().includes((formControl.value as string).toLocaleLowerCase());
        }
        else {
            return true;
        }
    }

    // private evaluateMultipleStringsFormControl(formControl: AbstractControl, valuesToCheck: string[]): boolean {
    //   if (formControl.value != null && formControl.value != '') {
    //     return valuesToCheck.some(valueFromPersonQuery => valueFromPersonQuery?.toLocaleLowerCase().includes((formControl.value.toString()).toLocaleLowerCase()));
    //   }
    //   else {
    //     return true;
    //   }
    // }

    private evaluateDateFormControl(fromDateForm: AbstractControl, toDateForm: AbstractControl, personQuery: PersonQuery): boolean {
        if (
            fromDateForm.value != null && fromDateForm.value != '' &&
			toDateForm.value != null && toDateForm.value != ''
        ) {
            const from = new Date(fromDateForm.value);
            const to = new Date(toDateForm.value);
            const createdDateTime = new Date(personQuery.createdDateTime);

            return from <= createdDateTime && createdDateTime <= to;
        }
        else if (fromDateForm.value != null && fromDateForm.value != '') {
            const from = new Date(fromDateForm.value);
            return from <= new Date(personQuery.createdDateTime);
        }
        else if (toDateForm.value != null && toDateForm.value != '') {
            const to = new Date(toDateForm.value);
            return new Date(personQuery.createdDateTime) >= to;
        } else
            return true;
    }

    public getStatusCount(status: string): number {
        if (!this.cachedFilterResponse)
            return 0;
        else
            return this.cachedFilterResponse.requestList.filter((x) => x.status === status).length;
    }

    public checkActiveStatus(status: string): boolean {
        return this.form.get('status').value === status;
    }


    // CLEANING FILTERS AND GLOBAL SEARCH \\

    clearFilter(key: string): void {
        this.form.controls[key].setValue('');
    }

    clearAllFilters(): void {
        const formControls = this.form.controls;
        const formKeys = Object.keys(formControls);

        formKeys.forEach(key => {
            formControls[key].setValue('');
        });
    }

    clearGlobalSearch(): void {
        if (this.lastInputReference) this.lastInputReference.value = '';
        this.query = '';
        this.search().catch(e => console.error(e));
    }

    clearAllFiltersAndGlobalSearch(): void {
        this.rowsPerPage = ROWS_PER_PAGE_DEFAULT;
        this.clearAllFilters();
        this.clearGlobalSearch();
    }

    setSortField(field: string, order: number): void {
        this.sortField.next(field);
        this.sortOrder.next(order);
    }

    resetSortField(): void {
        this.sortField.next('');
        this.sortOrder.next(1);
    }
}
